/**
 * Strip metadata (EXIF, incl. GPS + device info) from an uploaded image by
 * re-encoding it through a canvas — the canvas keeps only pixels, so all
 * metadata is dropped (#627 4.5).
 *
 * Two reasons this matters here:
 *  - Privacy: KYC photos and in-home work photos routinely carry GPS
 *    coordinates and device fingerprints in EXIF.
 *  - Hostile files: metadata blocks are a classic place to smuggle payloads;
 *    re-encoding removes them entirely.
 *
 * Only jpeg/png are re-encoded (PDFs and anything else pass through untouched).
 * Image failures are fail-closed because returning original bytes could expose
 * an in-home GPS location or device identifier.
 */
export async function stripImageMetadata(file: File, opts: { maxEdge?: number } = {}): Promise<File> {
  const type = file.type === 'image/jpg' ? 'image/jpeg' : file.type;
  if (type !== 'image/jpeg' && type !== 'image/png') return file;
  if (typeof document === 'undefined' || typeof createImageBitmap === 'undefined') {
    throw new Error('This browser cannot safely remove private image metadata. Upload a PDF or use a supported browser.');
  }

  try {
    const bitmap = await createImageBitmap(file);
    // `maxEdge` (optional) also scales the image down in the same re-encode,
    // for a destination with a byte cap. Never up.
    const scale = opts.maxEdge ? Math.min(1, opts.maxEdge / Math.max(bitmap.width, bitmap.height)) : 1;
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(bitmap.width * scale));
    canvas.height = Math.max(1, Math.round(bitmap.height * scale));
    const ctx = canvas.getContext('2d');
    if (!ctx) throw new Error('Private image metadata could not be removed.');
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height);
    bitmap.close?.();

    const blob = await new Promise<Blob | null>((resolve) =>
      // High quality for JPEG so an ID photo stays legible; PNG ignores quality.
      canvas.toBlob(resolve, type, type === 'image/jpeg' ? 0.92 : undefined),
    );
    if (!blob) throw new Error('Private image metadata could not be removed.');

    // The re-encode drops the photo's own metadata, but some encoders write
    // fresh metadata of their own: Safari on iPhone saves a canvas JPEG with
    // an EXIF (APP1) block. The upload functions refuse any APP1 at all, so
    // every iPhone job photo came back "still contains private location or
    // device metadata" (live, 22 Oct). Remove what the encoder added; colour
    // data (ICC, Adobe) stays so the picture still looks right.
    const bytes = removeMetadataSegments(new Uint8Array(await blobBytes(blob)), type);
    if (!bytes) throw new Error('Private image metadata could not be removed.');
    return new File([bytes], file.name, { type, lastModified: Date.now() });
  } catch (error) {
    if (error instanceof Error && /metadata/.test(error.message)) throw error;
    throw new Error('Private image metadata could not be removed. Try a different image or upload a PDF.');
  }
}

async function blobBytes(blob: Blob): Promise<ArrayBuffer> {
  if (typeof blob.arrayBuffer === 'function') return blob.arrayBuffer();
  return new Response(blob).arrayBuffer();
}

const PNG_SIGNATURE = [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a];
/** PNG chunks that carry text or EXIF; the server refuses all four. */
const PNG_METADATA_CHUNKS = new Set(['eXIf', 'tEXt', 'zTXt', 'iTXt']);

const startsWith = (bytes: Uint8Array, at: number, ascii: string) =>
  ascii.split('').every((c, i) => bytes[at + i] === c.charCodeAt(0));

/**
 * The encoded image without metadata segments, or null when it cannot be
 * walked (fail closed: the caller refuses rather than upload unchecked bytes).
 *
 * JPEG: keeps APP0 (JFIF), APP2 only when it is an ICC colour profile, and
 * APP14 (Adobe colour transform); drops every other APPn (APP1 is EXIF/XMP)
 * and comments. Everything from the scan onward is copied unchanged.
 * PNG: drops eXIf, tEXt, zTXt and iTXt chunks.
 */
export function removeMetadataSegments(bytes: Uint8Array, type: 'image/jpeg' | 'image/png'): Uint8Array<ArrayBuffer> | null {
  if (type === 'image/jpeg') {
    if (bytes.length < 4 || bytes[0] !== 0xff || bytes[1] !== 0xd8) return null;
    const kept: Uint8Array[] = [bytes.subarray(0, 2)];
    let offset = 2;
    while (offset + 4 <= bytes.length) {
      if (bytes[offset] !== 0xff) return null;
      const marker = bytes[offset + 1];
      if (marker === 0xff) { offset += 1; continue; } // fill byte
      if (marker === 0xda || marker === 0xd9) {
        kept.push(bytes.subarray(offset));
        return concat(kept);
      }
      if (marker === 0x01 || (marker >= 0xd0 && marker <= 0xd7)) { // standalone markers
        kept.push(bytes.subarray(offset, offset + 2));
        offset += 2;
        continue;
      }
      const length = (bytes[offset + 2] << 8) | bytes[offset + 3];
      if (length < 2 || offset + 2 + length > bytes.length) return null;
      const segment = bytes.subarray(offset, offset + 2 + length);
      const isApp = marker >= 0xe0 && marker <= 0xef;
      const keep = !isApp && marker !== 0xfe
        ? true
        : marker === 0xe0 || marker === 0xee || (marker === 0xe2 && startsWith(bytes, offset + 4, 'ICC_PROFILE\0'));
      if (keep) kept.push(segment);
      offset += 2 + length;
    }
    return null; // no scan: not an image this function produced
  }

  if (bytes.length < 8 || PNG_SIGNATURE.some((b, i) => bytes[i] !== b)) return null;
  const kept: Uint8Array[] = [bytes.subarray(0, 8)];
  let offset = 8;
  while (offset + 12 <= bytes.length) {
    const length = ((bytes[offset] << 24) | (bytes[offset + 1] << 16) | (bytes[offset + 2] << 8) | bytes[offset + 3]) >>> 0;
    if (length > bytes.length - offset - 12) return null;
    const name = String.fromCharCode(bytes[offset + 4], bytes[offset + 5], bytes[offset + 6], bytes[offset + 7]);
    if (!PNG_METADATA_CHUNKS.has(name)) kept.push(bytes.subarray(offset, offset + 12 + length));
    offset += 12 + length;
    if (name === 'IEND') return concat(kept);
  }
  return null;
}

function concat(parts: Uint8Array[]): Uint8Array<ArrayBuffer> {
  const out = new Uint8Array(parts.reduce((n, p) => n + p.length, 0));
  let at = 0;
  for (const p of parts) { out.set(p, at); at += p.length; }
  return out;
}
